/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

// ─────────────────────────────────────────────────────────────────────────────
// CalendarioLectura — calendario mensual con la portada del libro que se
// estaba leyendo cada día.
//
// Reglas:
//  - Un libro aparece en el calendario desde el día en que se marca "Leyendo"
//    hasta el día en que se marca otra cosa (o hasta hoy si sigue activo).
//  - Como máximo se muestran 2 libros por día (los dos que se empezaron antes).
//  - Si hay un tercer libro en paralelo, no aparece hasta que uno de los dos
//    primeros termina y le cede el hueco.
//  - Si no había ningún libro "Leyendo" ese día, la celda aparece vacía.
//
// Fuente de datos: estados_lectura/{libroId}_{userId}
//   → fechaInicioLectura / fechaFinLectura (ver EstadoLectura en App.js)
// ─────────────────────────────────────────────────────────────────────────────

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const DIAS_SEMANA = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];

const inicioDelDia = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

function Portadita({ libro }) {
  const [error, setError] = useState(false);
  const tienePortada = libro?.portada?.startsWith('https://') && !error;
  return tienePortada ? (
    <img src={libro.portada} alt="" onError={() => setError(true)}
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
  ) : (
    <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #1e1e2e, #141420)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ fontSize: '16px' }}>📖</span>
    </div>
  );
}

export function CalendarioLectura({ userId, onVerLibro }) {
  const hoy = new Date();
  const [mes, setMes] = useState(hoy.getMonth());
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [sesiones, setSesiones] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    if (!userId) return;
    let activo = true;
    setCargando(true);
    getDocs(query(collection(db, 'estados_lectura'), where('userId', '==', userId)))
      .then(snap => {
        const hoyInicio = inicioDelDia(new Date());
        const data = snap.docs
          .map(d => d.data())
          .filter(d => d.libroId && d.libro?.titulo && d.fechaInicioLectura?.toDate)
          .map(d => ({
            entrada: d,
            libroId: d.libroId,
            libro: d.libro,
            inicio: inicioDelDia(d.fechaInicioLectura.toDate()),
            fin: d.fechaFinLectura?.toDate ? inicioDelDia(d.fechaFinLectura.toDate()) : hoyInicio,
          }))
          .sort((a, b) => a.inicio - b.inicio);
        if (activo) { setSesiones(data); setCargando(false); }
      })
      .catch(() => activo && setCargando(false));
    return () => { activo = false; };
  }, [userId]);

  const cambiarMes = (delta) => {
    let m = mes + delta, a = anio;
    if (m < 0) { m = 11; a -= 1; }
    if (m > 11) { m = 0; a += 1; }
    setMes(m); setAnio(a);
  };

  const primerDiaSemana = (new Date(anio, mes, 1).getDay() + 6) % 7; // 0 = lunes
  const numDias = new Date(anio, mes + 1, 0).getDate();
  const celdas = [...Array(primerDiaSemana).fill(null), ...Array.from({ length: numDias }, (_, i) => i + 1)];
  const hoyInicio = inicioDelDia(hoy);

  const librosDelDia = (diaNum) => {
    const fecha = inicioDelDia(new Date(anio, mes, diaNum));
    if (fecha > hoyInicio) return [];
    return sesiones.filter(s => fecha >= s.inicio && fecha <= s.fin).slice(0, 2);
  };

  return (
    <div style={{ padding: '0 16px 40px', maxWidth: '620px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '24px 0 6px' }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#1DB954" style={{ flexShrink: 0 }}>
          <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z" />
        </svg>
        <div>
          <h1 style={{ fontSize: '26px', margin: 0, fontWeight: 900, color: 'white', lineHeight: 1 }}>Calendario de lectura</h1>
          <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#555' }}>Los libros que fuiste leyendo, día a día.</p>
        </div>
      </div>

      {/* Navegación de mes */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '18px', margin: '24px 0 16px' }}>
        <button onClick={() => cambiarMes(-1)}
          style={{ background: 'none', border: '1px solid #2a2a2a', color: '#aaa', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', fontSize: '15px' }}>‹</button>
        <span style={{ fontSize: '16px', fontWeight: 900, color: 'white', minWidth: '160px', textAlign: 'center' }}>{MESES[mes]} {anio}</span>
        <button onClick={() => cambiarMes(1)}
          style={{ background: 'none', border: '1px solid #2a2a2a', color: '#aaa', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', fontSize: '15px' }}>›</button>
      </div>

      {/* Tarjeta calendario */}
      <div style={{ background: 'linear-gradient(160deg, #141414 0%, #0a0a0a 100%)', border: '1px solid #1e1e1e', borderRadius: '16px', padding: '14px', boxShadow: '0 12px 30px rgba(0,0,0,0.4)' }}>
        {/* Cabecera días semana */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px' }}>
          {DIAS_SEMANA.map((d, i) => (
            <div key={d} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 'bold', color: i === 6 ? '#f44336' : '#555', padding: '4px 0' }}>{d}</div>
          ))}
        </div>

        {cargando ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
            {[...Array(35)].map((_, i) => <div key={i} style={{ aspectRatio: '0.78', background: '#111', borderRadius: '6px', animation: 'pulse 1.5s infinite' }} />)}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
            {celdas.map((diaNum, i) => {
              if (diaNum === null) return <div key={`vacio-${i}`} />;
              const libros = librosDelDia(diaNum);
              const esHoy = anio === hoy.getFullYear() && mes === hoy.getMonth() && diaNum === hoy.getDate();
              return (
                <div key={diaNum}
                  style={{
                    position: 'relative', aspectRatio: '0.78', borderRadius: '6px', overflow: 'hidden',
                    background: '#111', border: esHoy ? '1.5px solid #1DB954' : '1px solid #1a1a1a',
                    display: 'flex',
                  }}>
                  {libros.length === 0 ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '12px', color: esHoy ? '#1DB954' : '#333', fontWeight: esHoy ? 'bold' : 'normal' }}>{diaNum}</span>
                    </div>
                  ) : (
                    <>
                      {libros.map((s) => (
                        <div key={s.libroId}
                          onClick={() => onVerLibro && onVerLibro(s.entrada)}
                          title={`${s.libro?.titulo || ''}${s.libro?.autor ? ' — ' + s.libro.autor : ''}`}
                          style={{ flex: 1, cursor: onVerLibro ? 'pointer' : 'default' }}>
                          <Portadita libro={s.libro} />
                        </div>
                      ))}
                      <span style={{ position: 'absolute', top: '2px', left: '3px', fontSize: '9px', fontWeight: 'bold', color: 'white', textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>{diaNum}</span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {sesiones.length === 0 && !cargando && (
        <p style={{ color: '#333', fontSize: '13px', textAlign: 'center', margin: '20px 0 0' }}>
          Marca un libro como "Leyendo" en tu biblioteca y empezará a aparecer aquí.
        </p>
      )}
    </div>
  );
}
